import React, {useState, useEffect} from 'react';
import {makeRequest} from "../../utils/httpRequest";
import {formatDateString, formatDateToAmericanFormat, UserPermission, CompanyPermission} from "../../utils/utils";
import Config from "../../core/config";
import DatePicker from "react-datepicker";
import {useRecoilState} from "recoil";
import {loadingAtom} from "../../atoms";
import CustomerSelectAsync from "../customer/CustomerSelectAsync";
import {getCustomerById, downloadProductSaleReceipt} from "../../utils/requestUtils";
import {useNavigate} from "react-router-dom";
import Loading from "../../core/Loading";
import {usePermission} from "../../core/PermissionContext";
import NewButton from '../common/NewButton';
import SearchButton from '../common/SearchButton';
import TableRowActions from '../common/TableRowActions';
import ListPageLayout from '../common/ListPageLayout';
import { showConfirm, showAlert } from '../../core/dialog';

const apiUrl = Config.apiUrl;

const ProductSaleList = () => {
    const {hasUserPermission, hasCompanyPermission, loadingPermissions} = usePermission();
    const navigate = useNavigate();

    const [sales, setSales] = useState([]);
    const [customer, setCustomer] = useState(undefined);
    const [startDate, setStartDate] = useState(undefined);
    const [endDate, setEndDate] = useState(undefined);
    const [totalValue, setTotalValue] = useState(0);
    const [loading, setLoading] = useRecoilState(loadingAtom);

    async function makeQuery() {
        try {
            setLoading(true);
            const newStartDate = startDate ? startDate : new Date(1900, 0, 1);
            const newEndDate = endDate ? endDate : new Date(2100, 0, 1);
            const url = `${apiUrl}/api/product-sales/queryByDate?startDate=${formatDateToAmericanFormat(newStartDate)}&endDate=${formatDateToAmericanFormat(newEndDate)}&customerId=${customer ? customer.id : 0}`;
            const response = await makeRequest(url, 'GET');
            setSales(response);
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        makeQuery();
    }, []);

    useEffect(() => {
        setTotalValue(sales.reduce((acc, s) => acc + (s.totalValue || 0), 0));
    }, [sales]);

    async function removeSale(id) {
        try {
            await makeRequest(`${apiUrl}/api/product-sales/${id}`, 'DELETE');
            await makeQuery();
        } catch (error) {
            console.error('Error deleting:', error);
        }
    }

    const handleCustomerItemClick = async (id) => {
        const c = await getCustomerById(id);
        setCustomer(c);
    };

    const hasListPermission = () => hasUserPermission(UserPermission.LIST_PRODUCT_SALE) && hasCompanyPermission(CompanyPermission.LIST_PRODUCT_SALE);

    if (loadingPermissions) return <Loading />;
    if (!hasListPermission()) {
        navigate('/unauthorizedaccess');
        return null;
    }

    const getSaleActions = (sale) => [
        {
            key: 'entrega',
            label: 'Recibo',
            visible: hasUserPermission(UserPermission.DOWNLOAD_PRODUCT_SALE_RECEIPT) && hasCompanyPermission(CompanyPermission.DOWNLOAD_PRODUCT_SALE_RECEIPT),
            onClick: () => downloadProductSaleReceipt(sale.id, sale.code),
        },
        {
            key: 'edit',
            label: 'Editar',
            type: 'edit',
            visible: hasUserPermission(UserPermission.EDIT_PRODUCT_SALE) && hasCompanyPermission(CompanyPermission.EDIT_PRODUCT_SALE),
            onClick: () => navigate(`/app/productsale?id=${sale.id}`),
        },
        {
            key: 'delete',
            label: 'Excluir',
            type: 'delete',
            visible: hasUserPermission(UserPermission.REMOVE_PRODUCT_SALE) && hasCompanyPermission(CompanyPermission.REMOVE_PRODUCT_SALE),
            onClick: async () => {
                if (await showConfirm('Deseja realmente excluir esta venda?')) {
                    removeSale(sale.id);
                }
            },
            buttonProps: {className: 'remove-button'},
        },
    ];

    return (
        <ListPageLayout
            tableWidth={800}
            header={
                <div style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
                    <div className="header-label">Consulta de Vendas</div>
                    {hasUserPermission(UserPermission.CREATE_PRODUCT_SALE) && hasCompanyPermission(CompanyPermission.CREATE_PRODUCT_SALE) &&
                        <NewButton onClick={() => navigate('/app/productsale')} />
                    }
                </div>
            }
            filters={
                <div style={{display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'flex-end'}}>
                    <div className="list-page-filters-field" style={{width: '300px'}}>
                        <div>Cliente:</div>
                        <div className="list-page-filters-field-row" style={{marginTop: '5px'}}>
                            <CustomerSelectAsync
                                onItemClick={handleCustomerItemClick}
                                style={{width: '100%'}}
                                placeholder="Selecione um cliente..."
                                value={customer && customer.id ? customer.id : null}
                                prefilledCustomer={customer}
                            />
                            {customer && <button className="clean-button" onClick={() => setCustomer(undefined)}>Limpar</button>}
                        </div>
                    </div>
                    <div style={{marginTop: '5px'}}>
                        Data:
                        <div style={{display: 'flex', alignItems: 'center', marginTop: '5px', gap: '4px'}}>
                            <DatePicker
                                className="datepicker"
                                selected={startDate}
                                onChange={setStartDate}
                                dateFormat="dd/MM/yyyy"
                                isClearable
                                autoComplete="off"
                            />
                            <span>até</span>
                            <DatePicker
                                className="datepicker"
                                selected={endDate}
                                onChange={setEndDate}
                                dateFormat="dd/MM/yyyy"
                                isClearable
                                autoComplete="off"
                            />
                            <SearchButton onClick={makeQuery} />
                        </div>
                    </div>
                </div>
            }
            footer={
                <>
                    <div className="total-label">Total de Vendas: {sales.length}</div>
                    <div className="total-label">Valor Total: R$ {totalValue.toLocaleString('pt-BR', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</div>
                </>
            }
        >
            <table>
                <thead>
                    <tr>
                        <th>Código</th>
                        <th>Cliente</th>
                        <th>Data</th>
                        <th>Valor Total</th>
                        <th />
                    </tr>
                </thead>
                <tbody>
                    {sales.map(sale => (
                        <tr key={sale.id}>
                            <td>{sale.code}</td>
                            <td>{sale.customer?.name}</td>
                            <td>{formatDateString(sale.saleDate)}</td>
                            <td>
                                <div style={{textAlign: 'right'}}>
                                    R$ {sale.totalValue?.toLocaleString('pt-BR', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                                </div>
                            </td>
                            <td>
                                <TableRowActions actions={getSaleActions(sale)} />
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </ListPageLayout>
    );
};

export default ProductSaleList;
